import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sidebar } from "../sidebar/Sidebar";
import { Header } from "../header/Header";
import { tokens } from "../../theme";
import { useThemeMode } from "../../ThemeModeContext";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import RobotStuckAlerts from "../alerts/RobotStuckAlerts";

// Matches the `max-width: 768px` drawer rule in Sidebar.css. The two have to
// agree: JS owns the open/closed state, CSS owns the transform that shows it.
const MOBILE_BREAKPOINT = 768;

const MainLayout = () => {
  // Pages below <Outlet/> read `tokens.*` directly rather than via a hook, so
  // this subscription is what re-renders them when the theme toggles.
  useThemeMode();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeRobotCount, setActiveRobotCount] = useState(null);
  // Below the breakpoint the sidebar becomes an off-canvas overlay driven by
  // `mobileNavOpen` rather than by the desktop collapse state.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // Track the viewport crossing the breakpoint.
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close the drawer on navigation (a nav item was tapped), and whenever the
  // viewport grows back to desktop — otherwise the flag survives the resize and
  // the drawer reappears the next time the window narrows.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  // Escape closes the drawer — the expected dismissal for anything modal-ish.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  // The header's control: it opens the drawer on mobile and collapses the docked
  // rail on desktop.
  const handleToggleSidebar = () => {
    if (isMobile) setMobileNavOpen((v) => !v);
    else setSidebarOpen((v) => !v);
  };

  useEffect(() => {
    const fetchActive = async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setActiveRobotCount(data?.robots?.active ?? null);
        }
      } catch {
        /* silent */
      }
    };
    fetchActive();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: tokens.bg,
      }}
    >
      {/* activeRobotCount is passed through for a future header stat surface. */}
      <Header
        activeRobotCount={activeRobotCount}
        isMobile={isMobile}
        mobileNavOpen={mobileNavOpen}
        onToggleSidebar={handleToggleSidebar}
      />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Sidebar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={handleToggleSidebar}
          isMobile={isMobile}
          mobileNavOpen={mobileNavOpen}
          closeMobileNav={() => setMobileNavOpen(false)}
        />

        {/* Grey canvas the content card floats on. The card itself owns the
            scrolling, so this wrapper only supplies the inset gutter. */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: tokens.bg,
            // Matches .bq-sidebar's own 12px inset so the cards share a gutter.
            // On a phone that gutter is dead space — every horizontal pixel
            // belongs to the page itself — so it tightens up.
            padding: isMobile ? 8 : 12,
          }}
        >
          {/* Floating panel: same inset-card treatment as Header and Sidebar.
              scrollbarGutter: 'stable' reserves the scrollbar width so short and
              tall pages don't shift right-aligned content horizontally. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              display: "flex",
              flexDirection: "column",
              background: tokens.surface,
              border: `1px solid ${tokens.border}`,
              borderRadius: 18,
              boxShadow:
                "0 10px 30px -5px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(61, 106, 224, 0.04)",
            }}
          >
            {/* key on pathname replays the animation per route. `minHeight: 0`
                bounds viewport-sized pages (DeliveryLayout, PatrolPage) so this
                container doesn't scroll on top of their own scrolling. */}
            <div
              key={location.pathname}
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                animation: "bq-page-in .22s ease",
              }}
            >
              <Outlet />
            </div>
          </div>
        </div>
      </div>
      {/* "Robot may be stuck" help requests, shown on every page */}
      <RobotStuckAlerts />
      <style>{`@keyframes bq-page-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
};

export default MainLayout;
